import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { AlertTriangle, X, Save, User, Loader2 } from 'lucide-react';

const ProfileCompletionModal = ({ isOpen, onClose, onComplete }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchProfileStatus();
    }
  }, [isOpen]);

  const fetchProfileStatus = async () => {
    setLoading(true);
    try {
      const res = await api.get('/auth/profile-status');
      setProfileData(res.data);
      
      // Pre-fill form with current data
      if (res.data.current_data) {
        setFormData(res.data.current_data);
      }
    } catch (error) {
      console.error('Failed to fetch profile status');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const res = await api.put('/auth/complete-profile', formData);
      
      if (res.data.profile_incomplete) {
        toast.warning('Profile updated but still incomplete. Please fill all required fields.');
      } else {
        toast.success('Profile completed successfully!');
        onComplete?.();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('profile_completion_skipped', Date.now().toString());
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleSkip} />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden" data-testid="profile-completion-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-amber-100">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Complete Your Profile</h2>
                <p className="text-sm text-slate-500">Please provide the missing information</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleSkip}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : profileData?.missing_fields?.length > 0 ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Missing Fields Alert */}
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Missing fields:</strong>{' '}
                  {profileData.missing_fields.map(f => f.label).join(', ')}
                </p>
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Name Fields */}
                {profileData.missing_fields.some(f => f.field === 'name') && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name <span className="text-red-500">*</span></Label>
                      <Input
                        id="first_name"
                        value={formData.first_name || ''}
                        onChange={(e) => handleChange('first_name', e.target.value)}
                        placeholder="Enter first name"
                        data-testid="input-first-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name</Label>
                      <Input
                        id="last_name"
                        value={formData.last_name || ''}
                        onChange={(e) => handleChange('last_name', e.target.value)}
                        placeholder="Enter last name"
                        data-testid="input-last-name"
                      />
                    </div>
                  </>
                )}

                {/* Email */}
                {profileData.missing_fields.some(f => f.field === 'email') && (
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="email">Email Address <span className="text-red-500">*</span></Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="Enter email address"
                      data-testid="input-email"
                    />
                  </div>
                )}

                {/* Mobile */}
                {profileData.missing_fields.some(f => f.field === 'mobile_primary') && (
                  <div className="space-y-2">
                    <Label htmlFor="mobile_primary">Mobile Number <span className="text-red-500">*</span></Label>
                    <Input
                      id="mobile_primary"
                      value={formData.mobile_primary || ''}
                      onChange={(e) => handleChange('mobile_primary', e.target.value)}
                      placeholder="Enter mobile number"
                      data-testid="input-mobile"
                    />
                  </div>
                )}

                {/* Date of Birth */}
                {profileData.missing_fields.some(f => f.field === 'date_of_birth') && (
                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth">Date of Birth</Label>
                    <Input
                      id="date_of_birth"
                      type="date"
                      value={formData.date_of_birth || ''}
                      onChange={(e) => handleChange('date_of_birth', e.target.value)}
                      data-testid="input-dob"
                    />
                  </div>
                )}

                {/* Gender */}
                {profileData.missing_fields.some(f => f.field === 'gender') && (
                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender</Label>
                    <Select value={formData.gender || ''} onValueChange={(v) => handleChange('gender', v)}>
                      <SelectTrigger data-testid="select-gender">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Father's Name */}
                {profileData.missing_fields.some(f => f.field === 'father_name') && (
                  <div className="space-y-2">
                    <Label htmlFor="father_name">Father's Name</Label>
                    <Input
                      id="father_name"
                      value={formData.father_name || ''}
                      onChange={(e) => handleChange('father_name', e.target.value)}
                      placeholder="Enter father's name"
                      data-testid="input-father-name"
                    />
                  </div>
                )}

                {/* Aadhaar */}
                {profileData.missing_fields.some(f => f.field === 'aadhaar_number') && (
                  <div className="space-y-2">
                    <Label htmlFor="aadhaar_number">Aadhaar Number</Label>
                    <Input
                      id="aadhaar_number"
                      value={formData.aadhaar_number || ''}
                      onChange={(e) => handleChange('aadhaar_number', e.target.value)}
                      placeholder="Enter 12-digit Aadhaar"
                      maxLength={12}
                      data-testid="input-aadhaar"
                    />
                  </div>
                )}

                {/* PAN */}
                {profileData.missing_fields.some(f => f.field === 'pan_number') && (
                  <div className="space-y-2">
                    <Label htmlFor="pan_number">PAN Number</Label>
                    <Input
                      id="pan_number"
                      value={formData.pan_number || ''}
                      onChange={(e) => handleChange('pan_number', e.target.value.toUpperCase())}
                      placeholder="Enter PAN (e.g., ABCDE1234F)"
                      maxLength={10}
                      data-testid="input-pan"
                    />
                  </div>
                )}
              </div>
            </form>
          ) : (
            <div className="text-center py-8">
              <User className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
              <p className="text-lg font-medium text-slate-900">Your profile is complete!</p>
              <p className="text-sm text-slate-500 mt-1">All required information has been provided.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && profileData?.missing_fields?.length > 0 && (
          <div className="px-6 py-4 border-t bg-slate-50 flex items-center justify-between">
            <Button variant="ghost" onClick={handleSkip}>
              Skip for now
            </Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="save-profile-btn">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Profile
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileCompletionModal;
